import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import { candidateErrorMessage, createCandidate, updateCandidate } from '@shared/api/adminApi.ts';
import { Alert, Button } from '@shared/components/Button.tsx';
import { CandidateFields } from '@shared/components/CandidateFields.tsx';
import { Loading, Skeleton } from '@shared/components/Skeleton.tsx';
import { useToast } from '@shared/components/Toast.tsx';
import {
  type CandidateFormState,
  emptyCandidateForm,
  type FormErrors,
  formFromCandidate,
  toAdminCreateRequest,
  toUpdateRequest,
  validateCandidate,
} from '@shared/lib/candidateForm.ts';
import { districtLabel, typeLabel } from '@shared/lib/enums.ts';
import type { Candidate } from '@shared/types.ts';
import { AdminBanner } from '../Layout.tsx';
import { fetchCandidates, fetchDetails, invalidateCandidates, invalidateDetails } from '../data.ts';

function focusFirstError() {
  requestAnimationFrame(() => {
    const el = document.querySelector<HTMLElement>('.has-error input, .has-error select, .has-error textarea');
    el?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    el?.focus({ preventScroll: true });
  });
}

export function CandidateFormPage({ mode }: { mode: 'create' | 'edit' }) {
  const { id } = useParams();
  const navigate = useNavigate();
  const toast = useToast();

  const [form, setForm] = useState<CandidateFormState>(emptyCandidateForm);
  const [original, setOriginal] = useState<Candidate | null>(null);
  const [loadState, setLoadState] = useState<'loading' | 'ready' | 'notfound' | 'error'>(mode === 'edit' ? 'loading' : 'ready');
  const [errors, setErrors] = useState<FormErrors>({});
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string>();

  useEffect(() => {
    if (mode !== 'edit' || !id) return;
    let live = true;
    (async () => {
      try {
        const list = await fetchCandidates();
        const c = list.find((x) => x.id === id);
        if (!c) return live && setLoadState('notfound');
        invalidateDetails(c.id);
        const d = await fetchDetails(c);
        if (!live) return;
        setOriginal(c);
        setForm(formFromCandidate(c, d));
        setLoadState('ready');
      } catch {
        if (live) setLoadState('error');
      }
    })();
    return () => {
      live = false;
    };
  }, [mode, id]);

  const save = async () => {
    const e = validateCandidate(form, mode === 'edit' ? 'admin-edit' : 'admin-create');
    setErrors(e);
    if (Object.keys(e).length) return focusFirstError();
    setSaving(true);
    setSaveError(undefined);
    try {
      if (mode === 'create') {
        const { id: newId } = await createCandidate(toAdminCreateRequest(form));
        invalidateCandidates();
        toast('success', `${form.name} यांची नोंदणी झाली.`);
        navigate(`/candidates?view=${newId}`);
      } else if (original) {
        await updateCandidate(original.id, toUpdateRequest(form));
        invalidateCandidates();
        invalidateDetails(original.id);
        toast('success', 'बदल जतन झाले.');
        navigate(`/candidates?view=${original.id}`);
      }
    } catch (err) {
      setSaveError(candidateErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  const title = mode === 'create' ? 'नवीन स्पर्धक जोडा' : 'स्पर्धक संपादित करा';

  return (
    <>
      <AdminBanner title={title}
        sub={mode === 'create' ? 'स्पर्धकाच्या वतीने नोंदणी करा. नोंदणी निःशुल्क आहे.' : original?.name} />
      <div className='admin-container admin-content form-page'>
        <p><Link to='/candidates' className='text-link'>← स्पर्धक यादीकडे परत</Link></p>

        {loadState === 'loading' && (
          <Loading label='स्पर्धकाची माहिती लोड होत आहे…'>
            <div className='panel'>
              {Array.from({ length: 3 }, (_, s) => (
                <div key={s} style={{ marginBottom: 28 }}>
                  <Skeleton w={180} h={20} style={{ marginBottom: 18 }} />
                  <div className='field-grid'>
                    {[0, 1, 2, 3].map((i) => <div key={i}><Skeleton w='40%' h={14} style={{ marginBottom: 8 }} /><Skeleton h={48} /></div>)}
                  </div>
                </div>
              ))}
            </div>
          </Loading>
        )}
        {loadState === 'notfound' && <Alert tone='error' title='हा स्पर्धक सापडला नाही'>कदाचित तो हटवला गेला असेल.</Alert>}
        {loadState === 'error' && <Alert tone='error' title='माहिती लोड होऊ शकली नाही'>पान रीफ्रेश करून पुन्हा प्रयत्न करा.</Alert>}

        {loadState === 'ready' && (
          <div className='panel'>
            {mode === 'edit' && original && (
              <div className='readonly-strip'>
                <div><span>गट</span><strong>{typeLabel(original.candidate_type)}</strong></div>
                <div><span>मोबाईल</span><strong className='num'>{original.phone}</strong></div>
                <div><span>ईमेल</span><strong>{original.email ?? '—'}</strong></div>
                <div><span>जिल्हा</span><strong>{districtLabel(original.district)}</strong></div>
                <p className='readonly-strip__note'>
                  सर्व्हर फक्त नाव, जन्मतारीख, लिंग, भाषा, व्हॉट्सअ‍ॅप क्रमांक व गटाचा तपशील बदलू देतो. इतर माहिती बदलायची असल्यास स्पर्धक हटवून पुन्हा जोडा.
                </p>
              </div>
            )}

            {Object.keys(errors).length > 0 && (
              <div style={{ marginBottom: 18 }}>
                <Alert tone='error' title='काही माहिती अपूर्ण किंवा चुकीची आहे'>लाल रंगात दाखवलेली माहिती दुरुस्त करा.</Alert>
              </div>
            )}

            <CandidateFields
              value={form}
              onChange={(f) => {
                setForm(f);
                if (Object.keys(errors).length) setErrors(validateCandidate(f, mode === 'edit' ? 'admin-edit' : 'admin-create'));
              }}
              errors={errors}
              mode={mode === 'edit' ? 'admin-edit' : 'admin-create'}
              idPrefix='adm'
            />

            {saveError && <div style={{ marginBottom: 16 }}><Alert tone='error' title='जतन होऊ शकले नाही'>{saveError}</Alert></div>}

            <div className='form-actions'>
              <Link to='/candidates' className='btn btn-secondary btn-md'>रद्द करा</Link>
              <Button size='lg' icon='check' onClick={save} loading={saving} loadingText='जतन होत आहे…'>
                {mode === 'create' ? 'स्पर्धक जोडा' : 'बदल जतन करा'}
              </Button>
            </div>
          </div>
        )}
      </div>
    </>
  );
}
